/**
 * @fileoverview The letterbox missions table, server-side: filters, search,
 * sort and paging all go to `cityPlan.missionsPage`, so the page holds at
 * most one page of rows however many missions the plan has.
 *
 * Rows are rendered with the legacy row renderer, so the columns look
 * exactly as before; only where the data comes from changed.
 */
import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'

import DataFetchingTableTable from 'Components/DataFetchingTableTable'
import { authorization } from 'Module/Auth/Auth'
import { getMissionDisplayStatus } from 'shared/missionReadyToPublish'
import { formatLastName } from 'Utils/User'
import {
  BA_ASSIGNED,
  BA_UNASSIGNED,
  FILTER_BA,
  FILTER_STATUS,
  FILTER_WEEK,
  isSelected,
  statusFilterOptions,
} from 'Utils/CityPlan'
import { useCityPlanMissions } from 'Containers/Campaigns/CityPlan/hooks/useCityPlanMissions'
import letterboxMissionsTableRowRenderer from '../blocks/LetterboxMissionsTable/blocks/letterboxMissionsTableRowRenderer'
import { tableHeaders } from '../blocks/LetterboxMissionsTable/settings'
import { SelectionCheckbox } from './SelectionContext'
import InlineLabel from './InlineLabel'

export const PAGE_SIZE = 100

/** Headers that map to a server sort (see Utils/CityPlan ORDER_BY_MAP). */
const SORTABLE_HEADERS = ['Label', 'Date', 'Week', 'Status', 'BA']

/**
 * Which columns this user sees - the same pruning the legacy table does.
 * @param {object} campaign
 * @param {boolean} editable
 * @returns {Array<string>}
 */
export const headersFor = (campaign, editable) => {
  // tableHeaders holds a null entry for client admins; every header here
  // must be a string (DataFetchingTableTable upper-cases them for sorting).
  let headers = tableHeaders
    .filter(Boolean)
    .map(header => (header === 'Addresses' ? 'Households' : header))
  if (authorization.isCityManagerAssistant())
    headers = headers.filter(
      item =>
        !['Pay Adjustment', 'Bonus per conversion', 'Bonus threshold'].includes(
          item,
        ),
    )
  else if (authorization.isClientRole())
    headers = headers.filter(
      item =>
        ![
          'Select',
          'Density',
          'Households',
          'Pay Adjustment',
          'Bonus per conversion',
          'Bonus threshold',
          'Cost Per Flyer',
          'Date',
        ].includes(item),
    )
  else if (!campaign || !campaign.bonusEnabled)
    headers = headers.filter(
      item => !['Bonus per conversion', 'Bonus threshold'].includes(item),
    )
  if (!editable) headers = headers.filter(item => item !== 'Select')
  return headers
}

const isDefined = value => value !== undefined && value !== null

const MissionsTableV2 = ({
  cityPlanId,
  campaign,
  city,
  weeks,
  isoWeek,
  selection,
  onToggle,
  onPage,
  onDelete,
  onEditMeetingPoint,
  onRename,
  onRecolour,
  onEditRow,
  onEditArea,
  refreshRequestTrigger,
  editable,
  footer,
}) => {
  const headers = useMemo(() => headersFor(campaign, editable), [
    campaign,
    editable,
  ])

  const filters = useMemo(() => {
    const list = [
      { label: FILTER_STATUS, options: statusFilterOptions() },
      {
        label: FILTER_BA,
        options: [
          { label: 'Assigned', value: BA_ASSIGNED },
          { label: 'Unassigned', value: BA_UNASSIGNED },
        ],
      },
    ]
    if (!isoWeek)
      list.splice(1, 0, {
        label: FILTER_WEEK,
        options: (weeks || []).map(week => ({
          label: `W${week.isoWeek}, ${week.isoYear} (${week.total})`,
          value: week.key,
        })),
      })
    return list
  }, [weeks, isoWeek])

  const toRow = useCallback(
    mission =>
      letterboxMissionsTableRowRenderer({
        id: mission.id,
        label: mission.label,
        onCheckboxChange: onToggle,
        cancelled: false,
        status: getMissionDisplayStatus({
          status: mission.status,
          isReadyToPublish: mission.isReadyToPublish,
        }),
        isReadyToPublish: mission.isReadyToPublish,
        density: isDefined(mission.density)
          ? Math.round(mission.density)
          : null,
        addresses: mission.householdsAmount,
        payAdjustment: isDefined(mission.draftPayAdjustment)
          ? `${mission.draftPayAdjustment}`
          : '-',
        bonusPerConversion: null,
        bonusThreshold: null,
        costPerFlyer: mission.costPerFlyer,
        flyerCode: null,
        totalFlyersDistributed: mission.totalFlyersDistributed,
        packSize: null,
        week: mission.isoWeekKey
          ? `W${mission.isoWeekKey.split('-')[1]}, ${
              mission.isoWeekKey.split('-')[0]
            }`
          : '-',
        dateTime: mission.dateTime,
        isChecked: isSelected(selection, mission.id),
        baName: mission.user
          ? `${mission.user.firstName} ${formatLastName(mission.user.lastName)}`
          : null,
        baId: mission.user && mission.user.id,
        cityId: city && city.id,
        meetingPoint: mission.meetingPoint,
        onEditMeetingPoint,
        onDelete,
        timezone: city && city.timezone,
        boundary: null,
      }),
    [selection, onToggle, onEditMeetingPoint, onDelete, city],
  )

  const toRows = useCallback(
    (missions, { totalCount } = {}) => {
      const rows = missions.map(mission => {
        const row = toRow(mission)
        // The checkbox subscribes to the selection itself (see SelectionContext).
        row.Select = <SelectionCheckbox id={String(mission.id)} />
        if (onRename)
          row.Label = (
            <InlineLabel
              mission={mission}
              onRename={onRename}
              onRecolour={onRecolour}
            />
          )
        if (!isDefined(mission.costPerFlyer)) row['Cost Per Flyer'] = '-'
        if (!isDefined(mission.density)) row.Density = '-'
        if (!isDefined(mission.householdsAmount)) row.Addresses = '-'
        // Addresses -> Households, and "Edit" / "Edit area" actions next to
        // the rest.
        row.Households = row.Addresses
        const linkStyle = {
          background: 'none',
          border: 0,
          color: '#1878CF',
          cursor: 'pointer',
          padding: 0,
        }
        const showEditRow = Boolean(onEditRow)
        const showEditArea = Boolean(onEditArea && mission.hasBoundary)
        if (showEditRow || showEditArea)
          row.Actions = (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {row.Actions}
              {showEditRow && (
                <button
                  type="button"
                  className="link-button"
                  style={linkStyle}
                  title="Rename, re-date or adjust the pay of this mission"
                  onClick={() => onEditRow(mission)}
                >
                  Edit
                </button>
              )}
              {showEditArea && (
                <button
                  type="button"
                  className="link-button"
                  style={linkStyle}
                  onClick={() => onEditArea(mission)}
                >
                  Edit area
                </button>
              )}
            </div>
          )
        return row
      })
      // TableTable wants each row as an array of cells in header order.
      const toCells = row => headers.map(header => row[header])
      const footerRow = footer
        ? {
            ...footer,
            Label:
              totalCount === undefined
                ? footer.Label
                : totalCount.toLocaleString(),
          }
        : null
      return {
        data: rows.map(toCells),
        footer: footerRow ? [toCells(footerRow)] : undefined,
      }
    },
    [toRow, onRename, onRecolour, onEditRow, onEditArea, footer, headers],
  )

  const { fetchData } = useCityPlanMissions({
    cityPlanId,
    isoWeek,
    toRows,
    onPage,
  })

  return (
    <div style={{ maxWidth: '100%', overflowX: 'auto' }}>
      <DataFetchingTableTable
        header={headers}
        sortableHeaders={SORTABLE_HEADERS}
        filters={filters}
        fetchData={fetchData}
        resultsPerPage={PAGE_SIZE}
        showSearch
        searchPlaceholder="Search label"
        refreshRequestTrigger={refreshRequestTrigger}
      />
    </div>
  )
}

MissionsTableV2.propTypes = {
  cityPlanId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  campaign: PropTypes.object,
  city: PropTypes.object,
  weeks: PropTypes.array,
  isoWeek: PropTypes.string,
  selection: PropTypes.object.isRequired,
  onToggle: PropTypes.func.isRequired,
  onPage: PropTypes.func,
  onDelete: PropTypes.func,
  onEditMeetingPoint: PropTypes.func,
  onRename: PropTypes.func,
  onRecolour: PropTypes.func,
  onEditRow: PropTypes.func,
  onEditArea: PropTypes.func,
  refreshRequestTrigger: PropTypes.any,
  editable: PropTypes.bool,
  footer: PropTypes.object,
}

MissionsTableV2.defaultProps = {
  campaign: null,
  city: null,
  weeks: [],
  isoWeek: null,
  onPage: null,
  onDelete: null,
  onEditMeetingPoint: null,
  onRename: null,
  onRecolour: null,
  onEditRow: null,
  onEditArea: null,
  refreshRequestTrigger: null,
  editable: true,
  footer: null,
}

export default MissionsTableV2
